import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { Telemetry } from '~src/commonInfra/services/Telemetry';

import {
  DiscardDraftRequested,
  DraftFetchByParentIdFailed,
  DraftFetchByParentIdRequested,
  DraftsFetchInfoFailed,
  DraftsFetchInfoRequested
} from '../../app/actions/AppActionTypes';
import { ChannelTypes } from '../../home/types/ChannelTypes';
import { apiPerformanceTimer, HomeApiResult } from '../../home/utils/ApiPerformanceTimer';
import { generateUuid } from '../../home/utils/uuidGenerator';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { DiscardDraftRequestedAction, discardDraftsFailed, discardDraftSucceededAction, removeDraftFromContentInfo } from '../actions/DiscardDraftAction';
import {
  draftsAvailable,
  fetchDraftByParentIdFailed,
  FetchDraftByParentIdFailedAction,
  FetchDraftByParentIdRequestedAction,
  fetchDraftByParentIdSucceeded,
  fetchDraftsFailed,
  FetchDraftsFailedAction,
  FetchDraftsRequestedAction,
  fetchDraftsSucceeded,
} from '../actions/DraftsActions';
import { discardDraftRequest, getDraftByParentIdRequest,getDraftsRequest } from '../requests/DraftsRequests';
import { DraftsRequestFailedEvent, DraftsTelemetry, DraftsTelemetryEvents } from '../telemetry/DraftsTelemetry';
import { Draft } from '../types/DraftsTypes';

export function* rootDraftsSaga(): SagaIterator {
  yield takeEvery(DraftsFetchInfoRequested, handleDraftsRequestedAction);
  yield takeEvery(DraftsFetchInfoFailed, handleDraftsFailedAction);
  yield takeEvery(DiscardDraftRequested, handleDiscardDraftAction);
  yield takeEvery(DraftFetchByParentIdFailed, handleFetchDraftByParentIdFailedAction);
  yield takeEvery(DraftFetchByParentIdRequested, handleDraftByIdRequestedAction);
}

export function* handleDraftsRequestedAction(action: FetchDraftsRequestedAction): SagaIterator {
  try {
    const uuid = generateUuid();
    yield call(
      apiPerformanceTimer,
      fetchDraftsWithPerformanceTimer(action),
      action.spaceType,
      ChannelTypes.text,
      uuid
    );
  } catch (e) {
    yield put(fetchDraftsFailed(e));
  }
}

export function* handleDraftsFailedAction(action: FetchDraftsFailedAction): SagaIterator {
  sendDraftsErrorTelemetry(action);
}

export function* handleFetchDraftByParentIdFailedAction(action: FetchDraftByParentIdFailedAction) {
  sendDraftsErrorTelemetry(action);
}

function sendDraftsErrorTelemetry(action: FetchDraftsFailedAction | FetchDraftByParentIdFailedAction) {
  // TFS 1227824: Log any 5xx errors using Telemetry, but don't show an error to the user
  // via toast.
  if (action.error.status >= 500) {
    Telemetry.sendEvent<DraftsRequestFailedEvent>({
      type: DraftsTelemetryEvents.requestFailed,
      url: action.error.config.url,
      httpErrorCode: action.error.status,
      callstack: action.error.data.error.callstack,
      code: action.error.data.error.code,
      detail: action.error.data.error.detail,
      summary: action.error.data.error.summary
    });
  }
}

export function* handleDraftByIdRequestedAction(action: FetchDraftByParentIdRequestedAction): SagaIterator {
  try {
    const draft: Draft = yield call(getDraftByParentIdRequest, action);
    // Display of data is disabled by enableShowWorkbookAutoSaveOnVPC FF to enable shadow
    // deployment. This call would simply simulate loads on CDMS.
    if (FeatureFlags.enableShowWorkbookAutoSaveOnVPC) {
      yield put(fetchDraftByParentIdSucceeded(draft));
    }
  } catch (e) {
    yield put(fetchDraftByParentIdFailed(e));
  }
}

export function* handleDiscardDraftAction(action: DiscardDraftRequestedAction): SagaIterator {
  try {
    const contentId = yield call(discardDraftRequest, action);
    yield put(discardDraftSucceededAction(action.draftLuid));
    yield put(removeDraftFromContentInfo(action.parentLuid, action.contentType));
    yield call(DraftsTelemetry.sendDraftsDiscardEvent, {
      success: true,
      origin: action.locationOrigin,
      draft_luid: action.draftLuid,
      workbook_luid: action.parentLuid
    });
  }
  catch (e) {
  yield put(discardDraftsFailed(e));
  yield call(DraftsTelemetry.sendDraftsDiscardEvent, {
    success: false,
    error_message: JSON.stringify(e),
    origin: action.locationOrigin,
    draft_luid: action.draftLuid,
    workbook_luid: action.parentLuid
  });
  }
}

/**
 * High order function to return the function that uses the local action
 * without having to pass it down to the ApiPerformanceTimer
 * @param action
 */
export function fetchDraftsWithPerformanceTimer(action: FetchDraftsRequestedAction) {
  return function* fetchDrafts(result: HomeApiResult) {
    let drafts: Draft[] = yield call(getDraftsRequest, action);
    // Display of data is disabled by enableShowWorkbookAutoSaveOnVPC FF to enable shadow
    // deployment. This call would simply simulate loads on CDMS.
    if (!FeatureFlags.enableShowWorkbookAutoSaveOnVPC) {
      drafts = [];
    }
    yield put(fetchDraftsSucceeded(drafts));
    yield put(draftsAvailable(drafts));
    result.contentLength = drafts.length;
  };
}
